import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiEye,
  FiGitMerge,
  FiPlus,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createCrmContact,
  fetchCrmCompanies,
  fetchCrmContacts,
  mergeCrmContacts,
  updateCrmContact,
} from "../crmApi";
import CrmMergeModal from "../components/CrmMergeModal";
import { useCrmCopy } from "../crmI18n";
import ZadarmaCallButton from "../telephony/ZadarmaCallButton";
import WhatsAppStartButton from "../whatsapp/WhatsAppStartButton";

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company_id: "",
  job_title: "",
  department: "",
  crm_status: "active",
  source: "",
  notes: "",
};

const CONTACTS_PAGE_SIZE = 100;

function buildFormFromContact(contact) {
  return {
    first_name: contact?.first_name || "",
    last_name: contact?.last_name || "",
    email: contact?.email || "",
    phone: contact?.phone || "",
    company_id: contact?.company_id || "",
    job_title: contact?.job_title || "",
    department: contact?.department || "",
    crm_status: contact?.crm_status || "active",
    source: contact?.source || "",
    notes: contact?.notes || "",
  };
}

export default function Contacts() {
  const copy = useCrmCopy();
  const pageCopy = copy.entities.contacts;
  const navigate = useNavigate();
  const location = useLocation();
  const selectAllRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [query, setQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [mergeError, setMergeError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const statusOptions = useMemo(
    () => [
      { value: "active", label: copy.common.active },
      { value: "inactive", label: copy.common.inactive },
    ],
    [copy.common.active, copy.common.inactive]
  );

  const selectedContactIdSet = useMemo(() => new Set(selectedContactIds), [selectedContactIds]);
  const selectedContacts = useMemo(
    () => rows.filter((contact) => selectedContactIdSet.has(contact.id)),
    [rows, selectedContactIdSet]
  );
  const selectedContact = selectedContacts.length === 1 ? selectedContacts[0] : null;
  const selectedContactLabel =
    selectedContact?.full_name ||
    selectedContact?.email ||
    selectedContact?.phone ||
    (selectedContact ? `Contact ${selectedContact.id}` : "");
  const canMergeSelectedContacts = selectedContacts.length >= 2;
  const visibleContactIds = useMemo(() => rows.map((contact) => contact.id).filter(Boolean), [rows]);
  const allVisibleSelected =
    visibleContactIds.length > 0 && visibleContactIds.every((id) => selectedContactIdSet.has(id));
  const partiallySelected = selectedContactIds.length > 0 && !allVisibleSelected;
  const pageStart = totalRows ? pageIndex * CONTACTS_PAGE_SIZE + 1 : 0;
  const pageEnd = totalRows ? Math.min((pageIndex + 1) * CONTACTS_PAGE_SIZE, totalRows) : 0;
  const hasPreviousPage = pageIndex > 0;
  const hasNextPage = pageEnd < totalRows;

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchCrmContacts({
        q: query,
        include_inactive: showInactive ? 1 : 0,
        limit: CONTACTS_PAGE_SIZE,
        offset: pageIndex * CONTACTS_PAGE_SIZE,
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotalRows(Number(data?.total || 0));
    } catch (err) {
      console.error("[CRM] Contacts load error:", err);
      setError(err?.response?.data?.error || pageCopy.loadError);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [pageCopy.loadError, pageIndex, query, showInactive]);

  const loadCompanies = useCallback(async () => {
    try {
      const data = await fetchCrmCompanies({ limit: 200 });
      setCompanies(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      console.error("[CRM] Companies options load error:", err);
      setCompanies([]);
    }
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    const visibleIds = new Set(rows.map((contact) => contact.id));
    setSelectedContactIds((current) => {
      const next = current.filter((id) => visibleIds.has(id));
      return next.length === current.length ? current : next;
    });
  }, [rows]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = partiallySelected;
    }
  }, [partiallySelected]);

  const toggleContactSelection = (contactId) => {
    setSelectedContactIds((current) =>
      current.includes(contactId)
        ? current.filter((id) => id !== contactId)
        : [...current, contactId]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedContactIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !visibleContactIds.includes(id));
      }

      return Array.from(new Set([...current, ...visibleContactIds]));
    });
  };

  const clearContactSelection = () => {
    setSelectedContactIds([]);
  };

  const handleSelectedView = () => {
    if (selectedContact) navigate(`/crm/contacts/${selectedContact.id}`);
  };

  const handleSelectedEdit = () => {
    if (selectedContact) openEdit(selectedContact);
  };

  const openMergeModal = () => {
    if (!canMergeSelectedContacts) return;
    setMergeError("");
    setMergeModalOpen(true);
  };

  const closeMergeModal = () => {
    if (mergeSaving) return;
    setMergeModalOpen(false);
    setMergeError("");
  };

  const handleMergeSubmit = async (payload) => {
    setMergeSaving(true);
    setMergeError("");
    setError("");
    try {
      await mergeCrmContacts(payload);
      setSelectedContactIds([]);
      setMergeModalOpen(false);
      await loadContacts();
    } catch (err) {
      console.error("[CRM] Contact merge error:", err);
      setMergeError(err?.response?.data?.error || pageCopy.merge.error);
    } finally {
      setMergeSaving(false);
    }
  };

  const openCreate = () => {
    setEditingContact(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setForm(buildFormFromContact(contact));
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingContact(null);
    setForm(emptyForm);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      ...form,
      company_id: form.company_id || null,
    };

    try {
      if (editingContact?.id) {
        await updateCrmContact(editingContact.id, payload);
      } else {
        await createCrmContact(payload);
      }
      setModalOpen(false);
      setEditingContact(null);
      setForm(emptyForm);
      await loadContacts();
    } catch (err) {
      console.error("[CRM] Contact save error:", err);
      setError(err?.response?.data?.error || pageCopy.saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="crm-page crm-page--compactTable crm-page--contacts">
      <header className="crm-pageHeader">
        <div>
          <p className="crm-eyebrow">{copy.common.crm}</p>
          <h1>{copy.sections.contacts.title}</h1>
        </div>

        <button type="button" className="crm-ghostButton" onClick={openCreate}>
          <FiPlus aria-hidden="true" />
          <span>{pageCopy.newTitle}</span>
        </button>
      </header>

      <section className="crm-panel">
        <div className="crm-entityToolbar">
          <label className="crm-searchBox">
            <FiSearch aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPageIndex(0);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") loadContacts();
              }}
              placeholder={pageCopy.searchPlaceholder}
            />
          </label>
          <button type="button" className="crm-ghostButton" onClick={loadContacts} disabled={loading}>
            <FiSearch aria-hidden="true" />
            <span>{loading ? copy.common.loading : copy.common.search}</span>
          </button>
        </div>

        {error ? <div className="crm-stateBanner is-error">{error}</div> : null}

        <section className="crm-leadActionPanel" aria-label={pageCopy.selection.title}>
          <div className="crm-leadActionSummary">
            <strong>{pageCopy.selection.title}</strong>
            <span>
              {selectedContactIds.length
                ? pageCopy.selection.selected(selectedContactIds.length)
                : pageCopy.selection.noneSelected}
            </span>
          </div>
          <div className="crm-leadActionControls">
            <div className="crm-leadActionControl">
              <ZadarmaCallButton
                phones={selectedContact?.phone}
                label={selectedContactLabel}
                entityType="contact"
                entityId={selectedContact?.id}
                disabled={!selectedContact}
                disabledTitle={pageCopy.selection.singleOnly}
                showWhenUnavailable
                className="crm-leadActionCall"
              />
              <span>{pageCopy.selection.call}</span>
            </div>
            <div className="crm-leadActionControl">
              <WhatsAppStartButton
                phone={selectedContact?.phone}
                label={selectedContactLabel}
                contactId={selectedContact?.id}
                disabled={!selectedContact}
                disabledTitle={pageCopy.selection.singleOnly}
                className="crm-leadActionMini"
                onStarted={(conversation) => {
                  if (conversation?.id) navigate(`/crm/activity?conversation_id=${conversation.id}`);
                }}
              />
              <span>WhatsApp</span>
            </div>
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={handleSelectedView}
              disabled={!selectedContact}
              title={!selectedContact ? pageCopy.selection.singleOnly : pageCopy.detail?.viewDetails || copy.common.view}
              aria-label={pageCopy.detail?.viewDetails || copy.common.view}
            >
              <FiEye aria-hidden="true" />
              <span>{pageCopy.detail?.viewDetails || copy.common.view}</span>
            </button>
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={handleSelectedEdit}
              disabled={!selectedContact}
              title={!selectedContact ? pageCopy.selection.singleOnly : copy.common.edit}
              aria-label={copy.common.edit}
            >
              <FiEdit2 aria-hidden="true" />
              <span>{copy.common.edit}</span>
            </button>
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={openMergeModal}
              disabled={!canMergeSelectedContacts}
              title={!canMergeSelectedContacts ? pageCopy.selection.mergeMinimum : pageCopy.merge.title}
              aria-label={pageCopy.merge.title}
            >
              <FiGitMerge aria-hidden="true" />
              <span>{pageCopy.selection.merge}</span>
            </button>
            {selectedContactIds.length ? (
              <button type="button" className="crm-leadActionClear" onClick={clearContactSelection}>
                {pageCopy.selection.clear}
              </button>
            ) : null}
          </div>
        </section>

        <div className="crm-tableShell">
          <table className="crm-table">
            <thead>
              <tr>
                <th className="crm-selectCell">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    className="crm-tableCheckbox"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    disabled={!visibleContactIds.length}
                    aria-label={pageCopy.selection.selectVisible}
                  />
                </th>
                <th>{pageCopy.table.name}</th>
                <th>{pageCopy.table.company}</th>
                <th>{pageCopy.table.email}</th>
                <th>{pageCopy.table.phone}</th>
                <th>{pageCopy.table.status}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((contact) => {
                  const isInactiveContact = contact.crm_status === "inactive";
                  const isSelectedContact = selectedContactIdSet.has(contact.id);
                  const rowClassName = [
                    isInactiveContact ? "crm-tableRow--inactive" : "",
                    isSelectedContact ? "crm-tableRow--selected" : "",
                  ].filter(Boolean).join(" ");

                  return (
                    <tr key={contact.id} className={rowClassName || undefined}>
                      <td className="crm-selectCell">
                        <input
                          type="checkbox"
                          className="crm-tableCheckbox"
                          checked={isSelectedContact}
                          onChange={() => toggleContactSelection(contact.id)}
                          aria-label={pageCopy.selection.selectContact(
                            contact.full_name || contact.email || contact.phone || `Contact ${contact.id}`
                          )}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="crm-tablePrimaryButton"
                          onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                          aria-label={`${pageCopy.detail?.viewDetails || copy.common.view}: ${
                            contact.full_name || contact.email || contact.phone || `Contact ${contact.id}`
                          }`}
                        >
                          {contact.full_name || contact.email || contact.phone || "-"}
                        </button>
                        {contact.job_title ? (
                          <span className="crm-tableSecondary">{contact.job_title}</span>
                        ) : null}
                      </td>
                      <td>
                        {contact.company?.id ? (
                          <button
                            type="button"
                            className="crm-inlineLinkButton"
                            onClick={() =>
                              navigate(`/crm/companies?company_id=${contact.company.id}`, {
                                state: {
                                  returnTo: {
                                    pathname: location.pathname,
                                    search: location.search,
                                    label: copy.sections.contacts.title,
                                  },
                                },
                              })
                            }
                          >
                            {contact.company.name || copy.common.noCompany}
                          </button>
                        ) : (
                          contact.company?.name || copy.common.noCompany
                        )}
                      </td>
                      <td>{contact.email || "-"}</td>
                      <td>{contact.phone || "-"}</td>
                      <td>
                        <span className={`crm-statusPill crm-statusPill--${contact.crm_status || "active"}`}>
                          {contact.crm_status === "inactive" ? copy.common.inactive : copy.common.active}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6">
                    <div className="crm-tableEmpty">{loading ? copy.common.loading : pageCopy.empty}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="crm-paginationBar">
          <span>{pageCopy.pagination.summary(pageStart, pageEnd, totalRows)}</span>
          <div className="crm-paginationRight">
            <div className="crm-paginationControls">
              <button
                type="button"
                className="crm-ghostButton"
                onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                disabled={loading || !hasPreviousPage}
              >
                <FiChevronLeft aria-hidden="true" />
                <span>{pageCopy.pagination.previous}</span>
              </button>
              <span className="crm-paginationPage">{pageCopy.pagination.page(pageIndex + 1)}</span>
              <button
                type="button"
                className="crm-ghostButton"
                onClick={() => setPageIndex((prev) => prev + 1)}
                disabled={loading || !hasNextPage}
              >
                <span>{pageCopy.pagination.next}</span>
                <FiChevronRight aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className={`crm-ghostButton crm-inactiveToggle${showInactive ? " is-active" : ""}`}
              onClick={() => {
                setShowInactive((prev) => !prev);
                setPageIndex(0);
              }}
              disabled={loading}
            >
              {showInactive ? pageCopy.pagination.hideInactive : pageCopy.pagination.showInactive}
            </button>
          </div>
        </div>
      </section>

      <CrmMergeModal
        open={mergeModalOpen}
        copy={pageCopy.merge}
        items={selectedContacts}
        getItemLabel={(contact) =>
          contact?.full_name || contact?.email || contact?.phone || `Contact ${contact?.id || ""}`
        }
        saving={mergeSaving}
        error={mergeError}
        onClose={closeMergeModal}
        onSubmit={handleMergeSubmit}
      />

      {modalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleSubmit}>
            <div className="crm-modalHeader">
              <h2>{editingContact ? pageCopy.editTitle : pageCopy.newTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={pageCopy.fields.first_name}
                value={form.first_name}
                onChange={(value) => handleFieldChange("first_name", value)}
              />
              <Field
                label={pageCopy.fields.last_name}
                value={form.last_name}
                onChange={(value) => handleFieldChange("last_name", value)}
              />
              <Field
                label={pageCopy.fields.email}
                value={form.email}
                type="email"
                onChange={(value) => handleFieldChange("email", value)}
              />
              <Field
                label={pageCopy.fields.phone}
                value={form.phone}
                onChange={(value) => handleFieldChange("phone", value)}
              />
              <SelectField
                label={pageCopy.fields.company_id}
                value={form.company_id}
                onChange={(value) => handleFieldChange("company_id", value)}
                options={[
                  { value: "", label: copy.common.noCompany },
                  ...companies.map((company) => ({ value: company.id, label: company.name })),
                ]}
              />
              <Field
                label={pageCopy.fields.job_title}
                value={form.job_title}
                onChange={(value) => handleFieldChange("job_title", value)}
              />
              <Field
                label={pageCopy.fields.department}
                value={form.department}
                onChange={(value) => handleFieldChange("department", value)}
              />
              <SelectField
                label={pageCopy.fields.crm_status}
                value={form.crm_status}
                onChange={(value) => handleFieldChange("crm_status", value)}
                options={statusOptions}
              />
              <Field
                label={pageCopy.fields.source}
                value={form.source}
                onChange={(value) => handleFieldChange("source", value)}
              />
              <Field
                label={pageCopy.fields.notes}
                value={form.notes}
                textarea
                onChange={(value) => handleFieldChange("notes", value)}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeModal} disabled={saving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={saving}>
                {saving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </main>
  );
}

function Field({ label, value, onChange, type = "text", textarea = false }) {
  return (
    <label className={`crm-field ${textarea ? "crm-field--wide" : ""}`}>
      <span>{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={4} />
      ) : (
        <input type={type} value={value} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="crm-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
